import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, fromEvent, interval } from 'rxjs';

/** registerWhenStable 直後は SW がまだメッセージを受けられないことがある */
const CHECK_RETRY_DELAYS_MS = [0, 2_000, 8_000] as const;
/**
 * controller が無いと checkForUpdate は reject せず永遠に待ちうるため打ち切る。
 * @see NgswCommChannel.postMessage → worker.pipe(take(1))
 */
const CHECK_TIMEOUT_MS = 5_000;

/** 新しいビルドが用意できたらリロードし、フォアグラウンド復帰時にも更新を確認する */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private started = false;

  start(): void {
    if (this.started || !this.swUpdate.isEnabled) return;
    this.started = true;

    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY',
        ),
      )
      .subscribe(() => {
        document.location.reload();
      });

    this.swUpdate.unrecoverable.subscribe(() => {
      document.location.reload();
    });

    const appIsStable$ = this.appRef.isStable.pipe(
      first((isStable) => isStable === true),
    );
    // SPA はフルナビゲートしないので、安定後＋定期で明示チェックする
    concat(appIsStable$, interval(60 * 60 * 1000)).subscribe((value) => {
      // 安定直後だけリトライ（interval の数値は単発でよい）
      void this.checkForUpdateQuietly(value === true);
    });

    // ホーム画面 PWA は「閉じたつもり」でも生存することが多い
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => {
        void this.checkForUpdateQuietly(true);
      });
  }

  private async checkForUpdateQuietly(retryOnFailure: boolean): Promise<void> {
    const delays = retryOnFailure ? CHECK_RETRY_DELAYS_MS : ([0] as const);
    for (let i = 0; i < delays.length; i++) {
      const delayMs = delays[i];
      if (delayMs > 0) {
        await sleep(delayMs);
      }
      // controller 未確立なら postMessage が hang するのでスキップして次の試行へ
      if (
        typeof navigator === 'undefined' ||
        !('serviceWorker' in navigator) ||
        !navigator.serviceWorker.controller
      ) {
        continue;
      }
      try {
        await Promise.race([
          this.swUpdate.checkForUpdate(),
          sleep(CHECK_TIMEOUT_MS).then(() => {
            throw new Error('checkForUpdate timed out');
          }),
        ]);
        return;
      } catch {
        // SW 未準備・タイムアウトなどはリトライまたは次回のチェックに任せる
      }
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
